import type { EnvironmentKey } from "@visioni/a3s-environments";
import { RealmId } from "./realm-id";
import { RealmsolverConfig, RealmsolverConfigJson } from "./realmsolver-config";

/**
 * Production realmsolver configuration.
 *
 * This configuration determines how tenants are assigned to realms in production:
 * - All branches are pinned to R01 (legacy realm for existing tenants)
 * - New customers default to R02 (new realm for growth)
 */
export const REALMSOLVER_CONFIG_PRD: RealmsolverConfig = {
  rules: [
    // Branches always resolve to R01 (legacy realm)
    { kind: "pin", match: { kind: "branch", pattern: "*" }, realm: RealmId.fromNumber(0x01) },
  ],
  // New customers default to R02 (new realm)
  defaultRealms: [{ realm: RealmId.fromNumber(0x02), weight: 1 }],
};

/**
 * Non-production realmsolver configuration (Int, MR, Dev environments).
 *
 * All tenants resolve to RFF (the development/test realm).
 */
export const REALMSOLVER_CONFIG_NONPROD: RealmsolverConfig = {
  rules: [],
  defaultRealms: [{ realm: RealmId.fromNumber(0xff), weight: 1 }],
};

/**
 * JSON-serialized versions of configs for environment variables.
 *
 * Use these when setting REALMSOLVER_CONFIG in mise.toml or other env var contexts.
 */
export const REALMSOLVER_CONFIG_PRD_JSON: string = RealmsolverConfigJson.encode(REALMSOLVER_CONFIG_PRD);
export const REALMSOLVER_CONFIG_NONPROD_JSON: string = RealmsolverConfigJson.encode(REALMSOLVER_CONFIG_NONPROD);

/**
 * Get the realmsolver config for a given environment.
 *
 * Production uses a multi-realm config with routing rules;
 * all other environments use a single-realm nonprod config.
 */
export const getRealmsolverConfig = (environment: EnvironmentKey): RealmsolverConfig =>
  environment === "prd" ? REALMSOLVER_CONFIG_PRD : REALMSOLVER_CONFIG_NONPROD;

/**
 * Get the JSON-serialized realmsolver config for a given environment.
 */
export const getRealmsolverConfigJson = (environment: EnvironmentKey): string =>
  environment === "prd" ? REALMSOLVER_CONFIG_PRD_JSON : REALMSOLVER_CONFIG_NONPROD_JSON;
